import * as eff from 'redux-saga/effects';
import createActionCreators from 'redux-resource-action-creators';

import {
  actionTypes,
} from '../actions';
import {
  getResourceIds,
  getResourceMappedList,
} from '../selectors';
import {
  throwError,
  getEntityType,
} from './helpers';
import api from '../api';

const normalizedWorkouts = (programWorkouts) => programWorkouts.reduce(
  (acc, { workout, ...rest }) => {
    // TODO Fix
    if (!workout) {
      return acc;
    }

    return {
      resources: [
        ...acc.resources,
        rest,
      ],
      workouts: [
        ...acc.workouts,
        workout,
      ],
    };
  }, {
    resources: [],
    workouts: [],
  },
);

export default function* programFlow() {
  function* getProgram({ id, resourceType }) {
    const actions = createActionCreators('read', {
      resourceType,
      requestKey: 'getProgram',
    });

    yield eff.put(actions.pending({
      resources: [id],
    }));

    try {
      const program = yield eff.call(
        api.getEntity,
        {
          id,
          type: getEntityType(resourceType),
          qs: {
            nested: false,
          },
        },
      );

      yield eff.put(actions.succeeded({
        resources: [program],
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* getWeeks({ id, resourceType }) {
    const actions = createActionCreators('read', {
      resourceType: 'programWeeks',
      requestKey: 'getProgramWeeks',
    });

    yield eff.put(actions.pending());

    try {
      const resources = yield eff.call(
        api.getEntityInternals,
        {
          id,
          type: getEntityType(resourceType),
          endpoint: 'program_weeks',
          qs: {
            nested: false,
          },
        },
      );

      yield eff.put(actions.succeeded({
        resources,
        list: `program_${id}`,
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* getWorkouts({ id, resourceType }) {
    const actions = createActionCreators('read', {
      resourceType: 'programWorkouts',
      requestKey: 'getProgramWorkouts',
    });

    yield eff.put(actions.pending());

    try {
      const programWorkouts = yield eff.call(
        api.getEntityInternals,
        {
          id,
          type: getEntityType(resourceType),
          endpoint: 'program_workouts',
          qs: {
            nested: false,
          },
        },
      );

      const {
        resources,
        workouts,
      } = normalizedWorkouts(programWorkouts);

      const workoutType = resourceType === 'programTemplates'
        ? 'workoutTemplates'
        : 'personalWorkouts';

      yield eff.put(actions.succeeded({
        resources,
        includedResources: {
          [workoutType]: workouts,
        },
        customLists: {
          [workoutType]: `program_${id}`,
        },
        list: `program_${id}`,
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* getAssignments({ id, resourceType }) {
    const actions = createActionCreators('read', {
      resourceType: 'assignees',
      requestKey: 'getProgramAssignments',
    });

    yield eff.put(actions.pending());

    try {
      const {
        assignees,
        group_assignments: groupAssignments,
      } = yield eff.call(
        api.getEntityInternals,
        {
          id,
          type: getEntityType(resourceType),
          endpoint: 'program_assignments',
        },
      );

      yield eff.put(actions.succeeded({
        resources: assignees,
        includedResources: {
          groupAssignments,
        },
        list: `program_${id}`,
        customLists: {
          groupAssignments: `program_${id}`,
        },
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* addWorkout({
    payload,
    callbacks,
  }) {
    const resourceType = 'programWorkouts';
    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'createProgramWorkout',
    });
    try {
      yield eff.put(actions.pending());

      const newFields = {};
      const isPersonal = payload.program_type === 'PersonalProgram';
      if (!payload.workout_template_id && !payload.personal_workout_id) {
        const workout = yield eff.call(
          api.createEntity,
          {
            qs: {
              nested: false,
            },
            type: 'workout_templates',
            body: {
              name: payload.name || 'Workout',
              is_public: false,
              is_visible: false,
            },
          },
        );
        newFields.name = workout.name;
        newFields.workout_template_id = workout.id;
      }

      const {
        workout,
        ...restProgramWorkout
      } = yield eff.call(
        api.createEntity,
        {
          qs: {
            nested: false,
          },
          type: getEntityType(resourceType),
          body: {
            ...payload,
            ...newFields,
          },
        },
      );

      const programId = restProgramWorkout.program_id;

      const includedResources = {};
      const customLists = {};

      if (isPersonal) {
        includedResources.personalWorkouts = [workout];
        customLists.personalWorkouts = `program_${programId}`;
      } else {
        includedResources.workoutTemplates = [workout];
        customLists.workoutTemplates = `program_${programId}`;
      }

      yield eff.put(actions.succeeded({
        resources: [restProgramWorkout],
        includedResources,
        customLists,
        list: `program_${programId}`,
      }));

      callbacks?.onSuccess?.(restProgramWorkout);
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  function* duplicatePersonalProgramWorkout({ id, payload, callbacks }) {
    const resourceType = 'programWorkouts';
    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'createProgramWorkout',
    });

    try {
      yield eff.put(actions.pending());

      const {
        workout,
        ...restProgramWorkout
      } = yield eff.call(
        api.duplicateEntityItem,
        {
          id,
          type: 'program_workouts',
          body: payload,
        },
      );

      const {
        exercises,
        ...restWorkout
      } = workout;

      const programId = restProgramWorkout.program_id;

      const includedResources = {};
      const customLists = {};

      if (restProgramWorkout.workout_type === 'PersonalWorkout') {
        includedResources.personalWorkouts = [restWorkout];
        customLists.personalWorkouts = `program_${programId}`;
      } else {
        includedResources.workoutTemplates = [restWorkout];
        customLists.workoutTemplates = `program_${programId}`;
      }

      yield eff.put(actions.succeeded({
        resources: [restProgramWorkout],
        includedResources,
        customLists,
        list: `program_${programId}`,
      }));

      callbacks?.onSuccess?.(restProgramWorkout);
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  function* addWeek({ programId, programType }) {
    const resourceType = 'programWeeks';
    const listName = `program_${programId}`;

    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'addWeek',
    });

    const weeks = yield eff.select(
      getResourceIds(resourceType, listName),
    );

    try {
      yield eff.put(actions.pending());
      const response = yield eff.call(
        api.createEntity,
        {
          type: getEntityType(resourceType),
          body: {
            name: 'Week',
            position: weeks.length + 1,
            program_id: programId,
            program_type: programType,
          },
        },
      );

      yield eff.put(actions.succeeded({
        resources: [response],
        list: listName,
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* copyPersonalWorkout({ originalId, weekId }) {
    try {
      const duplicate = yield eff.call(
        api.duplicateEntityItem,
        {
          id: originalId,
          type: 'program_workouts',
          body: {
            week_id: weekId,
          },
          qs: {
            nested: false,
          },
        },
      );

      const copy = yield eff.call(
        api.updateEntity,
        {
          id: duplicate.id,
          type: 'program_workouts',
          body: {
            week_id: weekId,
          },
        },
      );

      return copy;
    } catch (e) {
      /* handle error */
      yield eff.call(throwError, e);
      return null;
    }
  }

  function* copyWeekWorkouts({ week, weekId }) {
    const programId = week.program_id;
    const list = `program_${programId}`;
    const resourceType = 'programWorkouts';
    const actions = createActionCreators('create', {
      list,
      requestKey: 'copyWeekWorkouts',
      resourceType,
    });

    try {
      yield eff.put(actions.pending());

      let programWorkouts = [];
      if (week.program_type === 'ProgramTemplate') {
        programWorkouts = yield eff.all(
          week.workouts.map(w => eff.call(
            api.createEntity,
            {
              type: 'program_workouts',
              body: {
                week_id: weekId,
                position: w.position,
                program_id: w.program_id,
                program_type: w.program_type,
                workout_type: w.workout_type,
                workout_template_id: w.workout_template_id || w.workout_id,
              },
              qs: {
                nested: false,
              },
            },
          )),
        );
      } else {
        const all = yield eff.all(
          week.workouts.map(w => eff.call(
            copyPersonalWorkout,
            {
              weekId,
              originalId: w.id,
            },
          )),
        );

        programWorkouts = all.filter(w => w?.id);
      }

      const {
        resources,
        workouts,
      } = normalizedWorkouts(programWorkouts);

      const workoutType = week.program_type === 'ProgramTemplate'
        ? 'workoutTemplates'
        : 'personalWorkouts';

      yield eff.put(actions.succeeded({
        resources,
        includedResources: {
          [workoutType]: workouts,
        },
        customLists: {
          [workoutType]: list,
        },
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* copyWeek({
    week,
    programId,
  }) {
    const resourceType = 'programWeeks';
    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'copyWeek',
    });
    try {
      yield eff.put(actions.pending());

      const response = yield eff.call(
        api.createEntity,
        {
          type: getEntityType(resourceType),
          body: {
            name: `${week.name} (copy)`,
            position: week.position + 1,
            program_id: week.program_id,
            program_type: week.program_type,
          },
        },
      );

      // copy workouts of week
      yield eff.fork(
        copyWeekWorkouts,
        {
          week,
          weekId: response.id,
        },
      );

      const allWeeks = yield eff.select(
        getResourceMappedList(
          'programWeeks',
          `program_${programId}`,
        ),
      );

      // update rest of week's position
      const programWeeks = yield eff.all(
        allWeeks
          .filter(w => w.position > week.position)
          .map(w => eff.call(
            api.updateEntity,
            {
              id: w.id,
              type: 'program_weeks',
              body: {
                position: w.position + 1,
              },
            },
          )),
      );

      yield eff.put(actions.succeeded({
        resources: [response],
        list: `program_${programId}`,
        includedResources: {
          programWeeks,
        },
        customLists: {
          programWeeks: `program_${programId}`,
        },
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  yield eff.takeEvery(actionTypes.REQUEST_PROGRAM, getProgram);
  yield eff.takeEvery(actionTypes.REQUEST_PROGRAM_WEEKS, getWeeks);
  yield eff.takeEvery(actionTypes.REQUEST_PROGRAM_WORKOUTS, getWorkouts);
  yield eff.takeEvery(actionTypes.REQUEST_PROGRAM_ASSIGNMENTS, getAssignments);
  yield eff.takeEvery(actionTypes.REQUEST_ADD_PROGRAM_WORKOUT, addWorkout);
  yield eff.takeEvery(actionTypes.REQUEST_ADD_PROGRAM_WEEK, addWeek);
  yield eff.takeEvery(actionTypes.REQUEST_COPY_PROGRAM_WEEK, copyWeek);
  yield eff.takeEvery(
    actionTypes.REQUEST_DUPLICATE_PERSONAL_PROGRAM_WORKOUT,
    duplicatePersonalProgramWorkout,
  );
}
